import axios from "axios";
import { getLocale } from "@/plugins/i18n.js";
import { isArray, isNonNullish } from "remeda";

/** @import { AxiosRequestConfig } from "axios" */

const formatEventForApi = (event) => {
  let apiEvent = {
    description: event.description,
    subtype: event.subtype,
    duration: event.duration,
  };

  if (event.timeSlots) {
    apiEvent.timeSlots = [];
    event.timeSlots.forEach((ts) => {
      apiEvent.timeSlots.push({
        datetime: ts.date + " " + ts.time,
        duration: ts.duration,
        break_duration: ts.break_duration,
      });
    });
  } else if (event.timeSlot) {
    let timeSlot = {
      datetime: event.timeSlot.date + " " + event.timeSlot.time,
      duration: event.timeSlot.duration,
    };

    if (event.timeSlot.break_duration) {
      timeSlot.break_duration = event.timeSlot.break_duration;
    }

    apiEvent.timeSlots = [timeSlot];
  }

  if (event.city) {
    if (event.city.id) {
      apiEvent.city = event.city.id;
    } else apiEvent.city = event.cityId;
  }

  return apiEvent;
};

export default {
  createTimeSlot(timeSlot, locale = null) {
    let newTs = {
      event: timeSlot.eventId,
      duration: timeSlot.duration,
      datetime: timeSlot.date + " " + timeSlot.time,
    };

    return axios.post(`/api/${getLocale(locale)}/time-slot`, newTs);
  },
  getToBePlannedEvents(locale = null) {
    return axios.get(`/api/${getLocale(locale)}/event/to_be_planned`);
  },

  getCalendarFilteredEvents(id, from, to, locale = null) {
    let rq =
      id === undefined
        ? `/api/${getLocale(locale)}/event/calendar`
        : `/api/${getLocale(locale)}/customer/${id}/event/calendar`;

    rq += `?dates[]=${from}&dates[]=${to}`;

    return axios.get(rq);
  },
  postUserEvent(event, locale = null) {
    return axios.post(
      `/api/${getLocale(locale)}/event/create`,
      formatEventForApi(event)
    );
  },
  postPickedEvent(id, locale = null) {
    return axios.post(`/api/${getLocale(locale)}/event/pickedcreate`, {
      parent: id,
    });
  },
  updateEvent(event, locale = null) {
    return axios.put(
      `/api/${getLocale(locale)}/event/${event.id}`,
      formatEventForApi(event)
    );
  },
  updateTimeSlot(timeSlot, locale = null) {
    return axios.put(`/api/${getLocale(locale)}/time-slot/${timeSlot.id}`, {
      datetime: timeSlot.date + " " + timeSlot.time,
      duration: timeSlot.duration,
      break_duration: timeSlot.break_duration,
    });
  },
  /**
   * @param {{search: string, department?: string, page?: number}} params
   * @param {string|null} locale
   * @param {AxiosRequestConfig} config
   */
  searchCity({ search, department, page = 1 }, locale = null, config = {}) {
    return axios.get(`/api/${getLocale(locale)}/city`, {
      ...config,
      params: { search, department, page },
    });
  },
  getWeekComputation(
    { weekNumber, year },
    customerId,
    locale = null,
    config = {}
  ) {
    let url = `/api/${getLocale(locale)}`;

    if (customerId !== undefined) {
      url += `/customer/${customerId}`;
    }

    return axios.get((url += `/week-compute/${year}/${weekNumber}`), config);
  },
  getRestWorkTime(year, locale = null) {
    return axios.get(`api/${getLocale(locale)}/rest_work_time/${year}`);
  },
  confirmEvent(eventId, parentOrderLineNumber = null, locale = null) {
    return axios.patch(
      `/api/${getLocale(locale)}/event/${eventId}/status/confirm`,
      null,
      { params: { parentOrderLineNumber } }
    );
  },
  doEvent(eventId, locale = null) {
    return axios.patch(`/api/${getLocale(locale)}/event/${eventId}/status/do`);
  },

  loadEvent(eventId, locale = null) {
    return axios.get(`/api/${getLocale(locale)}/event/${eventId}`);
  },

  scheduleTimeSlot(timeSlotId, timeSlotStart, timeSlotDuration, locale = null) {
    return axios.patch(
      `/api/${getLocale(locale)}/time-slot/${timeSlotId}/schedule`,
      {
        datetime: timeSlotStart,
        duration: timeSlotDuration,
      }
    );
  },
  scheduleMultiTimeSlot(eventId, timeSlots, quantity, locale = null) {
    let data = { timeSlots: timeSlots };

    if (quantity) {
      data.quantity = quantity;
    }

    return axios.patch(
      `/api/${getLocale(locale)}/event/${eventId}/schedule`,
      data
    );
  },
  defineRealTimeEvent(eventId, timeSlots, quantity, locale = null) {
    let data = {
      timeSlots: timeSlots,
    };

    if (quantity !== undefined) {
      data.quantity = quantity;
    }

    return axios.patch(
      `/api/${getLocale(locale)}/event/${eventId}/define-real-time`,
      data
    );
  },
  archiveEvent(eventId, locale = null) {
    return axios.patch(
      `/api/${getLocale(locale)}/event/${eventId}/status/archive`
    );
  },
  geteventTypes(locale = null) {
    return axios.get(`/api/${getLocale(locale)}/event-type`);
  },
  geteventTypesGroup(locale = null) {
    return axios.get(`/api/${getLocale(locale)}/event-type-group`);
  },
  getCalendarOwners(locale = null) {
    return axios.get(`api/${getLocale(locale)}/calendar-owner`);
  },
  getCalendarOwner(id, locale = null) {
    return axios.get(`api/${getLocale(locale)}/calendar-owner/${id}`);
  },
  getCredits(year, customerId, locale = null) {
    return axios.get(
      `api/${getLocale(locale)}/credit-value/${year}/customer/${customerId}`
    );
  },
  getEvents(locale = null) {
    return axios.get(`api/${getLocale(locale)}/event/`);
  },
  getFilteredEvents(
    filters,
    sortBy,
    sortDesc,
    page,
    limit,
    locale = null,
    options = {}
  ) {
    const { signal } = Object.assign(
      {
        signal: undefined,
      },
      options
    );

    const uri = `api/${getLocale(locale)}/event/`;

    const { data, params } = getSupervisionParams(
      filters,
      sortBy,
      sortDesc,
      page,
      limit
    );

    return axios.post(uri, data, {
      params,
      signal,
    });
  },
  checkFilteredEvents(filters, locale = null) {
    const uri = `api/${getLocale(locale)}/event/export/check`;

    const { data } = getSupervisionParams(filters);

    return axios.post(uri, data);
  },
  exportFilteredEvents(filters, sortBy, sortDesc, columns, locale = null) {
    const uri = `api/${getLocale(locale)}/event/export`;

    const { data, params } = getSupervisionParams(filters, sortBy, sortDesc);

    columns.forEach((column) => {
      params.append(`column[]`, column);
    });

    return axios.post(uri, data, { params, responseType: "blob" });
  },
  getManagers(locale = null) {
    return axios.get(`api/${getLocale(locale)}/manager`);
  },
  undoEvent(eventId, handler, locale = null) {
    axios
      .patch(`/api/${getLocale(locale)}/event/${eventId}/status/undo`)
      .then((response) => {
        handler(response.data);
      });
  },
  processEvent(eventId, locale = null) {
    return axios.patch(
      `/api/${getLocale(locale)}/event/${eventId}/status/processed`
    );
  },
  unblockEvent(eventId, locale = null) {
    return axios.patch(
      `/api/${getLocale(locale)}/event/${eventId}/status/unblock`
    );
  },
  unplanEvent(eventId, locale = null) {
    return axios.patch(
      `/api/${getLocale(locale)}/event/${eventId}/status/unplan`
    );
  },
  computeWeight(start, end, locale = null) {
    return axios.get(
      `/api/${getLocale(locale)}/weight?start=${start}&end=${end}`
    );
  },
  getBusinessInfo(eventId, locale = null) {
    return axios.get(`/api/${getLocale(locale)}/business-info/${eventId}`);
  },
  updateAssignment(eventId, auditorId, locale = null) {
    return axios.patch(
      `/api/${getLocale(locale)}/event/${eventId}/assignment`,
      {
        customer: auditorId,
      }
    );
  },
  updateComment(eventId, comment, locale = null) {
    return axios.patch(`/api/${getLocale(locale)}/event/${eventId}/comment`, {
      comment: comment,
    });
  },
  updateDescription(eventId, description, locale = null) {
    return axios.patch(
      `/api/${getLocale(locale)}/event/${eventId}/description`,
      {
        description: description,
      }
    );
  },
  updateQuantity(eventId, quantity, locale = null) {
    return axios.patch(`/api/${getLocale(locale)}/event/${eventId}/quantity`, {
      quantity: quantity,
    });
  },
  updateDeadline(eventId, deadline, locale = null) {
    return axios.patch(`/api/${getLocale(locale)}/event/${eventId}/deadline`, {
      deadline: deadline,
    });
  },
  updateConfirmedRs(eventId, confirmedRs, locale = null) {
    return axios.patch(
      `/api/${getLocale(locale)}/event/${eventId}/confirmed-rs`,
      {
        confirmedRs,
      }
    );
  },
  updateCreditCustomer(creditId, value, locale = null) {
    return axios.patch(`/api/${getLocale(locale)}/credit-value/${creditId}`, {
      value: value,
    });
  },
  getCustomerFolders(locale = null) {
    return axios.get(`/api/${getLocale(locale)}/customer/folder`);
  },
  getDepartments(locale = null) {
    return axios.get(`/api/${getLocale(locale)}/department`);
  },
  getBusinessGroups(locale = null) {
    return axios.get(`/api/${getLocale(locale)}/business-group`);
  },
  updateBusinessGroup(businessId, businessGroup, locale = null) {
    return axios.patch(
      `/api/${getLocale(locale)}/business/${businessId}/business-group`,
      {
        businessGroup,
      }
    );
  },
  checkEvent(eventId, locale = null) {
    return axios.get(`/api/${getLocale(locale)}/event/${eventId}/check`);
  },
  processEventRules(eventId, locale = null) {
    return axios.get(`/api/${getLocale(locale)}/event/${eventId}/process`);
  },
  getEventReferences(options = {}, locale = null) {
    const defaultOptions = {
      referenced: false,
    };

    options = Object.assign({ ...defaultOptions }, options);

    const params = new URLSearchParams();

    params.append("referenced", options.referenced);

    return axios.get(`/api/${getLocale(locale)}/event-reference`, { params });
  },
  searchBusinesses(params, locale = null) {
    return axios.get(`/api/${getLocale(locale)}/business`, { params });
  },
  getBusinessesByIds(ids, locale = null) {
    ids = ids.join(",");
    return axios.get(`/api/${getLocale(locale)}/business/${ids}`);
  },
  getEvent(id, locale = null) {
    return axios.get(`/api/${getLocale(locale)}/event/${id}`);
  },
  getChildrenEvents(parentId, locale = null) {
    return axios.get(`/api/${getLocale(locale)}/event/${parentId}/children`);
  },
  /**
   * @param {number|string} id
   * @param {string|null} locale
   * @param {AxiosRequestConfig} config
   */
  getLinkedEventsCv(id, locale = null, config = {}) {
    return axios.get(
      `/api/${getLocale(locale)}/linked-event/cv/event/${id}`,
      config
    );
  },
  /**
   * @param {string} type
   * @param {number|string} sourceId
   * @param {string|null} locale
   * @param {AxiosRequestConfig} config
   */
  getEventTypeLinkTargets(type, sourceId, locale = null, config = {}) {
    return axios.get(
      `/api/${getLocale(
        locale
      )}/event-type-link/${type}/event-type/${sourceId}/targets`,
      config
    );
  },
};

/**
 * @param {Map<any, any>} filters
 * @param {Array} sortBy
 * @param {Array} sortDesc
 * @param {number} page
 * @param {number} limit
 */
function getSupervisionParams(
  filters,
  sortBy = null,
  sortDesc = null,
  page = null,
  limit = null
) {
  const params = new URLSearchParams();

  if (isNonNullish(page)) {
    params.append("page", page.toString());
  }
  if (isNonNullish(limit)) {
    params.append("limit", limit.toString());
  }

  if (isNonNullish(sortBy) && isArray(sortBy)) {
    sortBy.forEach((value, index) => {
      params.append(`sortBy[]`, value);

      if (isNonNullish(sortDesc) && isArray(sortDesc)) {
        params.append(`sortDesc[]`, sortDesc.at(index));
      }
    });
  }

  const data = Object.fromEntries(filters);

  return {
    data,
    params,
  };
}
